/**
 * React hooks for permission management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { permissionsApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useAuthStore } from '@/store/auth';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import type {
    GrantPermissionRequest,
    RevokePermissionRequest,
    AssignBundleRequest,
    CreateSharedBundleRequest,
    UpdateBundleRequest,
} from '@pillar-portal/api-types';

const log = createComponentLogger('usePermissions');

/**
 * Fetch permissions for a role assignment
 */
export function useRoleAssignmentPermissions(roleAssignmentUuid: string | undefined) {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    return useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'permissions',
            'roleAssignment',
            roleAssignmentUuid,
        ]),
        queryFn: async () => await permissionsApi.getRoleAssignmentPermissions(roleAssignmentUuid!),
        enabled: !!roleAssignmentUuid,
    });
}

/**
 * Grant a permission to a role assignment
 */
export function useGrantPermission() {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (data: GrantPermissionRequest) => permissionsApi.grantPermission(data),
        onSuccess: () => {
            // The read endpoint is still keyed by role-assignment ID.
            // Invalidate the shared prefix until that tasklet lands.
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'permissions',
                        'roleAssignment',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });
}

/**
 * Revoke a permission from a role assignment
 */
export function useRevokePermission() {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (data: RevokePermissionRequest) => permissionsApi.revokePermission(data),
        onSuccess: () => {
            // The read endpoint is still keyed by role-assignment ID.
            // Invalidate the shared prefix until that tasklet lands.
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'permissions',
                        'roleAssignment',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });
}

/**
 * Fetch all shared permission bundles
 */
export function useSharedBundles() {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    return useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'permissions',
            'bundles',
            'shared',
        ]),
        queryFn: async () => await permissionsApi.listSharedBundles(),
        select: (data) => data.bundles,
    });
}

/**
 * Fetch a specific permission bundle
 */
export function usePermissionsBundle(bundleUuid: string | undefined) {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    return useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'permissions',
            'bundles',
            bundleUuid,
        ]),
        queryFn: async () => await permissionsApi.getBundle(bundleUuid!),
        enabled: bundleUuid !== undefined && bundleUuid.length > 0,
    });
}

/**
 * Create a shared permission bundle
 */
export function useCreateSharedBundle() {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (data: CreateSharedBundleRequest) => permissionsApi.createSharedBundle(data),
        onSuccess: () => {
            // Invalidate shared bundles list
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'permissions',
                        'bundles',
                        'shared',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });
}

/**
 * Update a permission bundle
 */
export function useUpdateBundle(bundleUuid: string) {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (data: UpdateBundleRequest) => permissionsApi.updateBundle(bundleUuid, data),
        onSuccess: () => {
            // Invalidate this specific bundle
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'permissions',
                        'bundles',
                        bundleUuid,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
            // Invalidate shared bundles list
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'permissions',
                        'bundles',
                        'shared',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });
}

/**
 * Delete a permission bundle
 */
export function useDeleteBundle() {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (bundleUuid: string) => permissionsApi.deleteBundle(bundleUuid),
        onSuccess: (_, bundleUuid) => {
            // Invalidate this specific bundle
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'permissions',
                        'bundles',
                        bundleUuid,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
            // Invalidate shared bundles list
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'permissions',
                        'bundles',
                        'shared',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });
}

/**
 * Assign a bundle to a role assignment
 */
export function useAssignBundle() {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (data: AssignBundleRequest) => permissionsApi.assignBundle(data),
        onSuccess: (_, variables) => {
            // Invalidate permissions for this role assignment
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'permissions',
                        'roleAssignment',
                        variables.role_assignment_uuid,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });
}

/**
 * Fetch role eligibility for actions
 */
export function useRoleEligibility(role: string | undefined) {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    return useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'permissions',
            'eligibility',
            role,
        ]),
        queryFn: async () => await permissionsApi.getRoleEligibility(role!),
        enabled: !!role,
    });
}
